import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  Badge,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { AttachFile, InsertDriveFile, Image, Audiotrack } from '@material-ui/icons';
import { useTheme, alpha } from '@mui/material/styles';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import http from '../../services/http';
import {
  normalizeNoticeRecord,
  buildServiceGroupLabelMap,
  shortAudienceLabel,
  fullAudienceLabel,
  htmlToPlainText,
} from '../../utils/noticeBoardAudience';
import { ConteudoHtml, pareceHtml } from '../../components/ConteudoFormatado';
import { toast } from 'react-toastify';
import FilePreview from '../../components/FilePreview';
import { hasAdminProfile } from '../../utils/profileRole';
import NovaObservacaoPeloMural from '../../components/NovaObservacaoPeloMural';
import { PERMISSAO_OBSERVACAO } from '../../components/ObservacaoDoCliente';
import { prazoDoComunicado } from '../../utils/avisosTemporarios';
import {
  ROTULO_DO_TIPO,
  TIPOS_PARA_FILTRAR,
  avisoCasaOTipo,
  rotuloDoTipoDoAviso,
} from '../../utils/tipoDoAviso';

const PERMISSION_CREATE_MURAL_NOTICES = '/criar-avisos-mural';

/*
 * Escrever a observação de um cliente é outra permissão, e não a de
 * publicar no Mural: quem pode escrever observação pode escrevê-la de
 * qualquer tela, e quem publica aviso não necessariamente pode mexer no
 * cadastro de cliente. A mesma que o painel do chamado já usa.
 */

function removeEmptyHtmlTags(html) {
  if (!html || typeof html !== 'string') return '';
  return html.replace(/<p><br><\/p>/g, '').trim();
}

function hasMeaningfulQuillContent(html) {
  const cleaned = removeEmptyHtmlTags(html);
  if (!cleaned) return false;
  const div = document.createElement('div');
  div.innerHTML = cleaned;
  return (div.textContent || '').trim().length > 0;
}

function sortByDateDesc(a, b) {
  const ta = new Date(a.publishedAt).getTime();
  const tb = new Date(b.publishedAt).getTime();
  return tb - ta;
}

function formatListDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDetailDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Mesma extração do `SanitizeHTML` logo abaixo: a regra de "isto é HTML?" é
// uma só, compartilhada com a central de ajuda.
const bodyLooksLikeHtml = pareceHtml;

const EMPTY_NOTICE_FILTERS = {
  // Ponto 37 da Gisele: categorizar por tipo, com o "Comunicado
  // temporario" entre eles. O tipo e DERIVADO de `kind` + `expiresAt`,
  // nao e coluna; ver utils/tipoDoAviso.
  tipo: '',
  subject: '',
  content: '',
  authorId: null,
  dateFrom: '',
  dateTo: '',
};

function startOfLocalDayMs(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const parts = dateStr.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
}

function endOfLocalDayMs(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const parts = dateStr.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
}

/*
  `SanitizeHTML` virou componente próprio (`components/ConteudoFormatado`)
  quando a central de ajuda (20/10) passou a renderizar artigo com o MESMO
  problema: HTML escrito por gente, que precisa ser sanitizado e precisa ficar
  legível nos dois temas.

  Extraído, não copiado: duas cópias da mesma regra de cor divergem no primeiro
  ajuste, e o sintoma seria o texto legível no mural e cinza-sobre-cinza na
  central — ou o contrário.
*/
const SanitizeHTML = ConteudoHtml;

const NoticeBoardPage = ({ currentUser, serviceGroups, permissions }) => {
  const theme = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [selectedId, setSelectedId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formKind, setFormKind] = useState('comunicado');
  const [formTitle, setFormTitle] = useState('');
  const [formBody, setFormBody] = useState('');
  const [formAudience, setFormAudience] = useState('all');
  const [formGroupSelection, setFormGroupSelection] = useState([]);
  const [formAttachments, setFormAttachments] = useState([]);
  const [formScheduledAt, setFormScheduledAt] = useState('');
  // O prazo do DESTAQUE no ícone -- não é prazo de vida do aviso.
  const [formExpiresAt, setFormExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [quickSearch, setQuickSearch] = useState('');
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...EMPTY_NOTICE_FILTERS }));
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState(() => ({ ...EMPTY_NOTICE_FILTERS }));
  const [filterAuthorUsers, setFilterAuthorUsers] = useState([]);

  const permissionsList = Array.isArray(permissions) ? permissions : [];
  const canManageMuralNotices =
    permissionsList.includes(PERMISSION_CREATE_MURAL_NOTICES) ||
    hasAdminProfile(currentUser) ||
    Boolean(currentUser?.is_super_admin);

  const podeEscreverObservacao = Boolean(currentUser?.is_super_admin)
    || permissionsList.includes(PERMISSAO_OBSERVACAO);
  const [observacaoAberta, setObservacaoAberta] = useState(false);

  const groupLabelById = useMemo(
    () => buildServiceGroupLabelMap(serviceGroups),
    [serviceGroups]
  );

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          ['bold', 'italic', 'underline', 'strike', 'blockquote'],
          [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
        ],
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    []
  );

  const dialogFieldSx = useMemo(
    () => ({
      '& .MuiInputLabel-root': { color: `${theme.palette.text.secondary} !important` },
      '& .MuiOutlinedInput-root': {
        color: `${theme.palette.text.primary} !important`,
        '& fieldset': { borderColor: `${theme.palette.divider} !important` },
        '&:hover fieldset': { borderColor: `${theme.palette.text.secondary} !important` },
        '&.Mui-focused fieldset': { borderColor: `${theme.palette.primary.main} !important` },
      },
      '& .MuiSelect-icon': { color: `${theme.palette.text.secondary} !important` },
    }),
    [theme]
  );

  const toolbarFieldSx = useMemo(
    () => ({
      ...dialogFieldSx,
      '& .MuiOutlinedInput-root': {
        ...dialogFieldSx['& .MuiOutlinedInput-root'],
        backgroundColor: `${theme.palette.background.paper} !important`,
      },
      '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        color: `${theme.palette.text.secondary} !important`,
      },
    }),
    [dialogFieldSx, theme]
  );

  const filteredItems = useMemo(() => {
    let list = items;
    const q = quickSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((item) => {
        const title = (item.title || '').toLowerCase();
        const body = htmlToPlainText(item.body || '').toLowerCase();
        const author = (item.authorName || '').toLowerCase();
        return title.includes(q) || body.includes(q) || author.includes(q);
      });
    }
    const { subject, content, authorId, dateFrom, dateTo, tipo } = appliedFilters;
    if (tipo) {
      list = list.filter((item) => avisoCasaOTipo(item, tipo));
    }
    if (subject.trim()) {
      const s = subject.trim().toLowerCase();
      list = list.filter((item) => (item.title || '').toLowerCase().includes(s));
    }
    if (content.trim()) {
      const c = content.trim().toLowerCase();
      list = list.filter((item) => htmlToPlainText(item.body || '').toLowerCase().includes(c));
    }
    if (authorId != null && !Number.isNaN(Number(authorId))) {
      const aid = Number(authorId);
      list = list.filter((item) => item.authorId != null && Number(item.authorId) === aid);
    }
    const fromMs = startOfLocalDayMs(dateFrom);
    const toMs = endOfLocalDayMs(dateTo);
    if (fromMs != null) {
      list = list.filter((item) => {
        const t = item.publishedAt ? new Date(item.publishedAt).getTime() : 0;
        return t >= fromMs;
      });
    }
    if (toMs != null) {
      list = list.filter((item) => {
        const t = item.publishedAt ? new Date(item.publishedAt).getTime() : 0;
        return t <= toMs;
      });
    }
    return list;
  }, [items, quickSearch, appliedFilters]);

  const hasActiveFilters = useMemo(() => {
    const { subject, content, authorId, dateFrom, dateTo, tipo } = appliedFilters;
    return (
      (tipo || '') !== '' ||
      subject.trim() !== '' ||
      content.trim() !== '' ||
      authorId != null ||
      (dateFrom || '').trim() !== '' ||
      (dateTo || '').trim() !== ''
    );
  }, [appliedFilters]);

  useEffect(() => {
    let cancelled = false;
    http
      .get('/users/list')
      .then((res) => {
        if (!cancelled) setFilterAuthorUsers(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setFilterAuthorUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get('/notice-board/announcements');
      const raw = Array.isArray(data) ? data : [];
      const list = raw.map((row) => normalizeNoticeRecord(row)).filter(Boolean);
      list.sort(sortByDateDesc);
      setItems(list);
    } catch {
      toast.error('Não foi possível carregar os avisos do mural.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  useEffect(() => {
    if (filteredItems.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !filteredItems.some((i) => i.id === selectedId)) {
      setSelectedId(filteredItems[0].id);
    }
  }, [filteredItems, selectedId]);

  /*
   * `?aviso=<id>`: abrir o mural JÁ NAQUELE aviso.
   *
   * É por aqui que o ícone de avisos temporários leva o operador ao aviso que
   * ele clicou. Sem isso, o mural abriria sempre no primeiro da lista, e ele
   * teria de procurar o que acabou de ler.
   *
   * Aplica UMA vez por id: depois disso o operador manda na seleção. Se ele
   * clicar em outro aviso do ícone, o id muda e a trava reabre -- é o mesmo
   * gesto, e ignorá-lo deixaria a tela sem reagir.
   */
  const avisoDaUrl = useMemo(
    () => new URLSearchParams(location.search).get('aviso'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.search],
  );
  const avisoDaUrlAplicado = useRef(null);

  useEffect(() => {
    if (!avisoDaUrl || avisoDaUrlAplicado.current === avisoDaUrl) return;

    const alvo = filteredItems.find((i) => String(i.id) === String(avisoDaUrl));

    // Enquanto a lista não chegou, não há o que selecionar -- e marcar a
    // trava aqui perderia o pedido.
    if (!alvo) return;

    setSelectedId(alvo.id);
    avisoDaUrlAplicado.current = avisoDaUrl;
  }, [avisoDaUrl, filteredItems]);

  const selected = filteredItems.find((i) => i.id === selectedId) || null;

  const resetForm = () => {
    setFormKind('comunicado');
    setFormTitle('');
    setFormBody('');
    setFormAudience('all');
    setFormGroupSelection([]);
    setFormAttachments([]);
    setFormScheduledAt('');
    setFormExpiresAt('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const handleCreate = async () => {
    const title = formTitle.trim();
    if (!title) {
      toast.error('Preencha o título.');
      return;
    }
    const hasText = hasMeaningfulQuillContent(formBody);
    const hasFiles = formAttachments.length > 0;
    if (!hasText && !hasFiles) {
      toast.error('Informe um texto na área de conteúdo ou anexe ao menos um arquivo.');
      return;
    }
    if (formAudience === 'groups' && formGroupSelection.length === 0) {
      toast.error('Selecione ao menos um departamento ou escolha "Todos".');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('kind', formKind);
      fd.append('title', title);
      fd.append('body', hasText ? removeEmptyHtmlTags(formBody) : '');
      fd.append('audience', formAudience);
      if (formAudience === 'groups') {
        formGroupSelection.forEach((g) => fd.append('service_group_ids[]', String(g.id)));
      }
      formAttachments.forEach((f) => fd.append('attachments[]', f));
      if (formScheduledAt) {
        fd.append('scheduled_at', new Date(formScheduledAt).toISOString());
      }
      if (formExpiresAt) {
        fd.append('expires_at', new Date(formExpiresAt).toISOString());
      }

      await http.post('/notice-board/announcements', fd);
      toast.success('Aviso publicado.');
      handleCloseDialog();
      await loadAnnouncements();
    } catch (err) {
      const d = err?.response?.data;
      const msg =
        typeof d?.message === 'string'
          ? d.message
          : d?.errors
            ? 'Verifique os anexos (máx. 10MB, tipos permitidos) e os campos.'
            : 'Não foi possível publicar o aviso.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!selected || selected.source !== 'database') return;
    setSaving(true);
    try {
      await http.delete(`/notice-board/announcements/${selected.id}`);
      toast.info('Aviso removido.');
      await loadAnnouncements();
    } catch (err) {
      const msg = err?.response?.data?.message;
      toast.error(typeof msg === 'string' ? msg : 'Não foi possível excluir o aviso.');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyNoticeFilters = () => {
    const { dateFrom, dateTo } = filterDraft;
    if (dateFrom && dateTo) {
      const a = startOfLocalDayMs(dateFrom);
      const b = endOfLocalDayMs(dateTo);
      if (a != null && b != null && a > b) {
        toast.error('A data inicial não pode ser posterior à data final.');
        return;
      }
    }
    setAppliedFilters({ ...filterDraft });
    setFilterDialogOpen(false);
  };

  const handleClearNoticeFilters = () => {
    setFilterDraft({ ...EMPTY_NOTICE_FILTERS });
    setAppliedFilters({ ...EMPTY_NOTICE_FILTERS });
  };

  const renderDetailAttachments = (attachments) => {
    if (!attachments || attachments.length === 0) return null;
    return (
      <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {attachments.map((att, idx) => {
          const name = att.original_name || att.path || `Arquivo ${idx + 1}`;
          const handleDownload = () => {
            http
              .get(`/notice-board/attachments/file?path=${encodeURIComponent(att.path)}`, { responseType: 'blob' })
              .then((res) => {
                const blob = res.data;
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = att.original_name || name;
                link.click();
                URL.revokeObjectURL(url);
              })
              .catch(() => toast.error('Erro ao baixar arquivo'));
          };
          const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(att.original_name || '');
          const isPdf = /\.pdf$/i.test(att.original_name || '');
          const isTxt = /\.txt$/i.test(att.original_name || '');
          const canPreview = isImage || isPdf || isTxt;
          const handleOpenPreview = canPreview
            ? () => {
                http
                  .get(`/notice-board/attachments/file?path=${encodeURIComponent(att.path)}`, { responseType: 'blob' })
                  .then((res) => {
                    const blob = res.data;
                    const mimeType =
                      blob.type || (isPdf ? 'application/pdf' : isTxt ? 'text/plain' : 'image/webp');
                    const file = new File([blob], att.original_name || name, { type: mimeType });
                    setPreviewFile(file);
                    setPreviewOpen(true);
                  })
                  .catch(() => toast.error('Erro ao abrir arquivo'));
              }
            : undefined;
          return (
            <Box key={idx}>
              {canPreview ? (
                <Button
                  size="small"
                  startIcon={isImage ? <Image /> : <InsertDriveFile />}
                  onClick={handleOpenPreview}
                  sx={{
                    textTransform: 'none',
                    color: `${theme.palette.primary.main} !important`,
                    '& .MuiButton-startIcon': { color: `${theme.palette.primary.main} !important` },
                  }}
                >
                  {name}
                </Button>
              ) : (
                <Button
                  size="small"
                  startIcon={
                    att.original_name && /\.(mp3|ogg|wav|webm)$/i.test(att.original_name) ? (
                      <Audiotrack />
                    ) : (
                      <InsertDriveFile />
                    )
                  }
                  onClick={handleDownload}
                  sx={{
                    textTransform: 'none',
                    color: `${theme.palette.primary.main} !important`,
                    '& .MuiButton-startIcon': { color: `${theme.palette.primary.main} !important` },
                  }}
                >
                  {name}
                </Button>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        p: 2,
        width: '100%',
        maxWidth: '100%',
        mx: 0,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <Typography
        variant="h5"
        sx={{ mb: 2, fontWeight: 600, flexShrink: 0, color: `${theme.palette.text.primary} !important` }}
      >
        Mural de Avisos
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flex: 1,
          width: '100%',
          minHeight: { xs: 480, sm: 'calc(100vh - 140px)' },
          maxHeight: { sm: 'calc(100vh - 140px)' },
          bgcolor: theme.palette.background.paper,
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', sm: 300 },
            maxWidth: { xs: '100%', sm: 300 },
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: { xs: 'min(42vh, 320px)', sm: 'none' },
            borderRight: { sm: `1px solid ${theme.palette.divider}` },
            borderBottom: { xs: `1px solid ${theme.palette.divider}`, sm: 'none' },
            bgcolor: alpha(theme.palette.text.primary, 0.06),
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, letterSpacing: 0.3, color: `${theme.palette.text.primary} !important` }}
            >
              Mural
            </Typography>
            {/*
              * Escrever a observação de um cliente daqui (ponto 35, terceira
              * parte). Ate hoje so dava pelo chamado ou pelo FAB.
              */}
            {podeEscreverObservacao && (
              <Tooltip title="Observação de cliente">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setObservacaoAberta(true)}
                    aria-label="Observação de cliente"
                    sx={{ color: `${theme.palette.text.primary} !important` }}
                  >
                    <PersonOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {canManageMuralNotices && (
              <Tooltip title="Novo aviso">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setDialogOpen(true)}
                    aria-label="Novo aviso"
                    disabled={saving}
                    sx={{ color: `${theme.palette.text.primary} !important` }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          <Divider />
          <Box sx={{ px: 1.25, py: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Pesquisar avisos…"
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value)}
              disabled={loading || items.length === 0}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, color: `${theme.palette.text.secondary} !important` }} />
                  </InputAdornment>
                ),
              }}
              sx={toolbarFieldSx}
              aria-label="Pesquisar no mural"
            />
            <Tooltip title="Filtros (assunto, conteúdo, autor, período)">
              <span>
                <Badge color="primary" variant="dot" invisible={!hasActiveFilters}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setFilterDraft({ ...appliedFilters });
                      setFilterDialogOpen(true);
                    }}
                    disabled={loading || items.length === 0}
                    aria-label="Abrir filtros do mural"
                    sx={{ color: `${theme.palette.text.primary} !important` }}
                  >
                    <FilterListIcon fontSize="small" />
                  </IconButton>
                </Badge>
              </span>
            </Tooltip>
          </Box>
          <Divider />
          <List dense disablePadding sx={{ overflow: 'auto', flex: 1, py: 0.5 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} sx={{ color: `${theme.palette.primary.main} !important` }} />
              </Box>
            ) : items.length === 0 ? (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="body2" sx={{ color: `${theme.palette.text.secondary} !important` }}>
                  Nenhum aviso ainda.
                </Typography>
              </Box>
            ) : filteredItems.length === 0 ? (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="body2" sx={{ color: `${theme.palette.text.secondary} !important` }}>
                  Nenhum aviso corresponde à pesquisa ou aos filtros.
                </Typography>
              </Box>
            ) : (
              filteredItems.map((item) => {
                const isSel = item.id === selectedId;
                const nb = theme.palette.noticeBoard;
                return (
                  <ListItemButton
                    key={item.id}
                    selected={isSel}
                    onClick={() => setSelectedId(item.id)}
                    alignItems="flex-start"
                    sx={{
                      py: 1.25,
                      px: 2,
                      borderLeft: isSel
                        ? `3px solid ${nb?.listSelectedBorder ?? theme.palette.primary.main}`
                        : '3px solid transparent',
                      '&.Mui-selected': {
                        backgroundColor: `${nb?.listSelectedBg ?? alpha(theme.palette.primary.main, 0.12)} !important`,
                        '&:hover': {
                          backgroundColor: `${nb?.listSelectedBgHover ?? alpha(theme.palette.primary.main, 0.18)} !important`,
                        },
                      },
                    }}
                  >
                    <ListItemText
                      primaryTypographyProps={{
                        sx: {
                          fontWeight: isSel ? 600 : 500,
                          lineHeight: 1.35,
                          color: `${theme.palette.text.primary} !important`,
                        },
                      }}
                      secondaryTypographyProps={{
                        component: 'div',
                        sx: { color: `${theme.palette.text.secondary} !important` },
                      }}
                      primary={item.title}
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{ color: `${theme.palette.text.secondary} !important` }}
                          >
                            {formatListDate(item.publishedAt)}
                          </Typography>
                          <Chip
                            label={rotuloDoTipoDoAviso(item)}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              borderColor: `${theme.palette.divider} !important`,
                              color: `${theme.palette.text.secondary} !important`,
                              '& .MuiChip-label': { px: 0.75, color: `${theme.palette.text.secondary} !important` },
                            }}
                          />
                          {item.isScheduled && (
                            <Chip
                              label={`Agendado · ${formatListDate(item.scheduledAt)}`}
                              size="small"
                              variant="outlined"
                              color="warning"
                              sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                            />
                          )}
                          <Chip
                            label={shortAudienceLabel(item, groupLabelById)}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              maxWidth: 160,
                              borderColor: `${theme.palette.divider} !important`,
                              color: `${theme.palette.text.secondary} !important`,
                              '& .MuiChip-label': {
                                px: 0.75,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: `${theme.palette.text.secondary} !important`,
                              },
                            }}
                          />
                          {item.attachments?.length > 0 && (
                            <Chip
                              label={`${item.attachments.length} anexo(s)`}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                borderColor: `${theme.palette.divider} !important`,
                                color: `${theme.palette.text.secondary} !important`,
                                '& .MuiChip-label': { color: `${theme.palette.text.secondary} !important` },
                              }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                );
              })
            )}
          </List>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            width: '100%',
            minHeight: { xs: 200, sm: 0 },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          {selected ? (
            <Box
              sx={{
                width: '100%',
                maxWidth: '100%',
                px: { xs: 2, sm: 4, md: 6 },
                py: { xs: 3, sm: 4 },
                boxSizing: 'border-box',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      display: 'block',
                      letterSpacing: 1,
                      mb: 0.5,
                      color: `${theme.palette.text.secondary} !important`,
                    }}
                  >
                    {formatDetailDate(selected.publishedAt)}
                  </Typography>
                  {selected.authorName ? (
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 0.75,
                        lineHeight: 1.5,
                        color: `${theme.palette.text.secondary} !important`,
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 600, color: `${theme.palette.text.secondary} !important` }}>
                        Autor:{' '}
                      </Box>
                      {selected.authorName}
                    </Typography>
                  ) : null}
                  <Typography
                    variant="h5"
                    component="h1"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.25,
                      color: `${theme.palette.text.primary} !important`,
                    }}
                  >
                    <span data-testid="aviso-aberto">{selected.title}</span>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      lineHeight: 1.5,
                      color: `${theme.palette.text.secondary} !important`,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ fontWeight: 600, color: `${theme.palette.text.secondary} !important` }}
                    >
                      Público:{' '}
                    </Box>
                    {fullAudienceLabel(selected, groupLabelById)}
                  </Typography>
                  {/*
                    * O prazo do destaque, quando houver (pedido do dono do
                    * produto, 20/10). O rótulo fala de DESTAQUE e não de
                    * validade: quem está lendo o comunicado precisa saber que
                    * ele não vai sumir daqui -- só sai do ícone.
                    */}
                  {prazoDoComunicado(selected.expiresAt) && (
                    <Typography
                      variant="body2"
                      data-testid="prazo-do-comunicado"
                      sx={{ color: `${theme.palette.text.secondary} !important`, mt: 0.25 }}
                    >
                      <Box component="span" sx={{ fontWeight: 600 }}>Destaque:{' '}</Box>
                      {prazoDoComunicado(selected.expiresAt)}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <Chip
                    label={rotuloDoTipoDoAviso(selected)}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: `${theme.palette.divider} !important`,
                      backgroundColor: `${alpha(theme.palette.text.primary, 0.06)} !important`,
                      color: `${theme.palette.text.primary} !important`,
                      '& .MuiChip-label': {
                        px: 1,
                        color: `${theme.palette.text.primary} !important`,
                        fontWeight: 600,
                      },
                    }}
                  />
                  {/*
                    Observação de cliente NÃO se exclui daqui: a linha é
                    espelho do campo no cadastro do cliente, e apagá-la no
                    mural deixaria o atendimento mostrando uma observação que o
                    mural já não tem. Quem limpa é o cadastro -- e a api também
                    recusa (422), então o botão aqui só prometeria algo que não
                    acontece.
                  */}
                  {canManageMuralNotices && selected.source === 'database' && !selected.companyId && (
                    <Tooltip title="Excluir aviso">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={handleDeleteSelected}
                          aria-label="Excluir"
                          disabled={saving}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              <Divider sx={{ mb: 3 }} />
              {selected.body && bodyLooksLikeHtml(selected.body) ? (
                <SanitizeHTML htmlContent={selected.body} theme={theme} />
              ) : selected.body ? (
                <Typography
                  component="div"
                  variant="body1"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.75,
                    fontSize: '1rem',
                    color: `${theme.palette.text.primary} !important`,
                  }}
                >
                  {selected.body}
                </Typography>
              ) : null}
              {renderDetailAttachments(selected.attachments)}
            </Box>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
              <Typography sx={{ color: `${theme.palette.text.secondary} !important` }}>
                {loading
                  ? 'Carregando…'
                  : items.length > 0 && filteredItems.length === 0
                    ? 'Nenhum aviso corresponde à pesquisa ou aos filtros.'
                    : 'Selecione um aviso na lista.'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/*
        * A observação do cliente escrita pelo Mural (ponto 35).
        *
        * IRMÃO do diálogo de "Novo aviso", nunca filho: aninhado nele, só
        * montava quando aquele estava aberto, e o botão do cabeçalho não abria
        * nada. Foi assim que subiu para produção em 26/10 -- o teste da página
        * afirmava que o BOTÃO aparece, e nenhum que ele ABRE a janela.
        */}
      <NovaObservacaoPeloMural
        aberto={observacaoAberta}
        onFechar={() => setObservacaoAberta(false)}
        onSalvou={loadAnnouncements}
      />

      <Dialog
        open={dialogOpen}
        onClose={() => {
          if (!saving) handleCloseDialog();
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            bgcolor: `${theme.palette.background.paper} !important`,
            backgroundImage: 'none',
          },
        }}
      >

        <DialogTitle sx={{ color: `${theme.palette.text.primary} !important` }}>Novo aviso</DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pt: 1,
            color: `${theme.palette.text.primary} !important`,
          }}
        >
          <TextField
            select
            label="Tipo"
            value={formKind}
            onChange={(e) => setFormKind(e.target.value)}
            fullWidth
            size="small"
            disabled={saving}
            sx={dialogFieldSx}
          >
            <MenuItem value="release">Release</MenuItem>
            <MenuItem value="comunicado">Comunicado</MenuItem>
          </TextField>

          <FormControl component="fieldset" variant="standard">
            <FormLabel
              component="legend"
              sx={{
                mb: 0.5,
                fontSize: '0.875rem',
                color: `${theme.palette.text.secondary} !important`,
                '&.Mui-focused': { color: `${theme.palette.text.secondary} !important` },
              }}
            >
              Publicar para
            </FormLabel>
            <RadioGroup
              value={formAudience}
              onChange={(e) => {
                const v = e.target.value;
                setFormAudience(v);
                if (v === 'all') setFormGroupSelection([]);
              }}
              sx={{
                color: `${theme.palette.text.primary} !important`,
                '& .MuiFormControlLabel-label': { color: `${theme.palette.text.primary} !important` },
              }}
            >
              <FormControlLabel
                value="all"
                control={<Radio size="small" disabled={saving} />}
                label="Todos os departamentos"
              />
              <FormControlLabel
                value="groups"
                control={<Radio size="small" disabled={saving} />}
                label="Departamentos específicos"
              />
            </RadioGroup>
          </FormControl>

          {formAudience === 'groups' && (
            <Autocomplete
              multiple
              disabled={saving}
              options={Array.isArray(serviceGroups) ? serviceGroups : []}
              getOptionLabel={(o) => o?.description || String(o?.id ?? '')}
              isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
              value={formGroupSelection}
              onChange={(_, v) => setFormGroupSelection(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Grupos de atendimento"
                  placeholder="Selecione…"
                  sx={dialogFieldSx}
                />
              )}
            />
          )}

          <TextField
            label="Agendar publicação (opcional)"
            type="datetime-local"
            value={formScheduledAt}
            onChange={(e) => setFormScheduledAt(e.target.value)}
            fullWidth
            size="small"
            disabled={saving}
            InputLabelProps={{ shrink: true }}
            helperText="Se preenchido, o aviso só aparece a partir desta data/hora."
            sx={dialogFieldSx}
          />

          {/*
            * O aviso TEMPORÁRIO (Gisele, 19/10/2026). O prazo não apaga o
            * aviso: ele só decide até quando o recado fica em destaque no
            * ícone ao lado do sino. No mural ele continua, como qualquer
            * outro -- e o texto de apoio diz isso, porque "expira" sozinho
            * seria lido como "some".
            */}
          <TextField
            label="Destacar no ícone até (opcional)"
            type="datetime-local"
            value={formExpiresAt}
            onChange={(e) => setFormExpiresAt(e.target.value)}
            fullWidth
            size="small"
            disabled={saving}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': 'Destacar no ícone até' }}
            helperText="Para avisos mais importantes: aparece no ícone ao lado do sino até esta data/hora. Depois, continua no mural."
            sx={dialogFieldSx}
          />

          <TextField
            label="Título"
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            fullWidth
            size="small"
            disabled={saving}
            sx={dialogFieldSx}
          />

          <Box>
            <Typography
              variant="caption"
              sx={{ display: 'block', mb: 0.75, color: `${theme.palette.text.secondary} !important` }}
            >
              Conteúdo
            </Typography>
            <Box
              sx={{
                '& .ql-toolbar': {
                  border: `1px solid ${theme.palette.divider} !important`,
                  borderBottom: 'none',
                  borderRadius: '12px 12px 0 0',
                  backgroundColor: `${theme.palette.background.default} !important`,
                  padding: '10px 12px',
                },
                '& .ql-toolbar .ql-stroke': {
                  stroke: `${theme.palette.text.primary} !important`,
                },
                '& .ql-toolbar .ql-fill': {
                  fill: `${theme.palette.text.primary} !important`,
                },
                '& .ql-toolbar .ql-picker-label': {
                  color: `${theme.palette.text.primary} !important`,
                },
                '& .ql-toolbar .ql-picker-options': {
                  backgroundColor: `${theme.palette.background.paper} !important`,
                  color: `${theme.palette.text.primary} !important`,
                },
                '& .ql-container': {
                  border: `1px solid ${theme.palette.divider} !important`,
                  borderTop: 'none',
                  borderRadius: '0 0 12px 12px',
                  fontSize: '15px',
                  fontFamily: 'inherit',
                  backgroundColor: `${theme.palette.background.paper} !important`,
                },
                '& .ql-editor': {
                  minHeight: 140,
                  padding: '10px 14px',
                  color: `${theme.palette.text.primary} !important`,
                  backgroundColor: `${theme.palette.background.paper} !important`,
                },
                '& .ql-editor strong, & .ql-editor b': { fontWeight: 700 },
                '& .ql-editor em, & .ql-editor i': { fontStyle: 'italic' },
                '& .ql-editor.ql-blank::before': {
                  fontStyle: 'normal',
                  color: `${theme.palette.text.disabled} !important`,
                },
              }}
              onPasteCapture={(e) => {
                const files = e.clipboardData?.files;
                if (files?.length) {
                  const list = Array.from(files);
                  setFormAttachments((prev) => [...prev, ...list]);
                  e.preventDefault();
                  e.stopPropagation();
                  toast.success(`${list.length} arquivo(s) colado(s).`, { position: 'bottom-center', autoClose: 2500 });
                }
              }}
            >
              <ReactQuill
                theme="snow"
                value={formBody}
                onChange={setFormBody}
                modules={quillModules}
                readOnly={saving}
                placeholder="Digite o aviso ou cole imagem/arquivo (Ctrl+V nos anexos)"
              />
            </Box>

            <input
              type="file"
              ref={fileInputRef}
              multiple
              accept="image/*,.pdf,.doc,.docx,.txt,audio/*,.mp3,.ogg,.webm,.wav"
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = e.target.files ? Array.from(e.target.files) : [];
                setFormAttachments((prev) => [...prev, ...files]);
                e.target.value = '';
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
              <Tooltip title="Anexar imagem, documento ou áudio (máx. 10MB)" arrow>
                <Button
                  size="small"
                  startIcon={<AttachFile />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={saving}
                  sx={{
                    textTransform: 'none',
                    color: `${theme.palette.primary.main} !important`,
                    '& .MuiButton-startIcon': { color: `${theme.palette.primary.main} !important` },
                  }}
                >
                  Anexar arquivos
                </Button>
              </Tooltip>
              {formAttachments.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                  {formAttachments.map((file, idx) => (
                    <Chip
                      key={`${file.name}-${idx}`}
                      size="small"
                      label={file.name}
                      onDelete={() => setFormAttachments((prev) => prev.filter((_, i) => i !== idx))}
                      sx={{
                        maxWidth: 200,
                        borderColor: `${theme.palette.divider} !important`,
                        color: `${theme.palette.text.secondary} !important`,
                        '& .MuiChip-label': { color: `${theme.palette.text.secondary} !important` },
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 1, color: `${theme.palette.text.secondary} !important` }}
            >
              Palavras: {Math.max(0, formBody.trim() ? formBody.replace(/<[^>]+>/g, ' ').trim().split(/\s+/).filter(Boolean).length : 0)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDialog}
            disabled={saving}
            sx={{ color: `${theme.palette.text.secondary} !important` }}
          >
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? 'Enviando…' : 'Publicar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: `${theme.palette.background.paper} !important`,
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ color: `${theme.palette.text.primary} !important` }}>Filtrar mural</DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pt: 1,
            color: `${theme.palette.text.primary} !important`,
          }}
        >
          {/*
            * O filtro por tipo (ponto 37 da Gisele). Em primeiro lugar no
            * dialogo porque e o recorte mais grosso: escolhido o tipo, os
            * campos de texto refinam dentro dele.
            *
            * `select` nativo do MUI, e nao Autocomplete: sao quatro opcoes
            * fixas, e o Autocomplete do dialogo existe para a lista de
            * autores, que tem dezenas.
            */}
          <TextField
            select
            label="Tipo"
            size="small"
            fullWidth
            value={filterDraft.tipo}
            onChange={(e) => setFilterDraft((d) => ({ ...d, tipo: e.target.value }))}
            sx={dialogFieldSx}
            SelectProps={{ SelectDisplayProps: { 'data-testid': 'filtro-tipo' } }}
          >
            <MenuItem value="">Todos</MenuItem>
            {TIPOS_PARA_FILTRAR.map((t) => (
              <MenuItem key={t} value={t}>{ROTULO_DO_TIPO[t]}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Assunto"
            size="small"
            fullWidth
            value={filterDraft.subject}
            onChange={(e) => setFilterDraft((d) => ({ ...d, subject: e.target.value }))}
            sx={dialogFieldSx}
            placeholder="Contém no título"
          />
          <TextField
            label="Conteúdo"
            size="small"
            fullWidth
            value={filterDraft.content}
            onChange={(e) => setFilterDraft((d) => ({ ...d, content: e.target.value }))}
            sx={dialogFieldSx}
            placeholder="Contém no texto (sem HTML)"
          />
          <Autocomplete
            size="small"
            options={filterAuthorUsers}
            getOptionLabel={(u) => u?.name || String(u?.id ?? '')}
            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
            value={
              filterDraft.authorId != null
                ? filterAuthorUsers.find((u) => Number(u.id) === Number(filterDraft.authorId)) || null
                : null
            }
            onChange={(_, v) =>
              setFilterDraft((d) => ({
                ...d,
                authorId: v != null && v.id != null ? Number(v.id) : null,
              }))
            }
            renderOption={(props, option) => (
              <li {...props}>
                <ListItemText
                  primary={option.name}
                  secondary={option.service_group?.description || undefined}
                  primaryTypographyProps={{
                    sx: { color: `${theme.palette.text.primary} !important` },
                  }}
                  secondaryTypographyProps={{
                    sx: { color: `${theme.palette.text.secondary} !important` },
                  }}
                />
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Autor" placeholder="Selecione…" sx={dialogFieldSx} />
            )}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              label="Publicado a partir de"
              type="date"
              size="small"
              value={filterDraft.dateFrom}
              onChange={(e) => setFilterDraft((d) => ({ ...d, dateFrom: e.target.value }))}
              sx={{ ...dialogFieldSx, flex: '1 1 200px', minWidth: 0 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Publicado até"
              type="date"
              size="small"
              value={filterDraft.dateTo}
              onChange={(e) => setFilterDraft((d) => ({ ...d, dateTo: e.target.value }))}
              sx={{ ...dialogFieldSx, flex: '1 1 200px', minWidth: 0 }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Typography variant="caption" sx={{ color: `${theme.palette.text.secondary} !important` }}>
            A pesquisa rápida na lista combina com estes filtros. Campos vazios são ignorados.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            onClick={handleClearNoticeFilters}
            sx={{ color: `${theme.palette.text.secondary} !important`, mr: 'auto' }}
          >
            Limpar filtros
          </Button>
          <Button onClick={() => setFilterDialogOpen(false)} sx={{ color: `${theme.palette.text.secondary} !important` }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleApplyNoticeFilters}>
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewFile(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            maxWidth: '95vw',
          },
        }}
      >
        {previewFile && (
          <FilePreview
            selectedFile={previewFile}
            setSelectedFile={() => {
              setPreviewOpen(false);
              setPreviewFile(null);
            }}
            isChat={false}
          />
        )}
      </Dialog>
    </Box>
  );
};

const mapState = (state) => ({
  currentUser: state.user.currentUser,
  serviceGroups: state.serviceGroups?.serviceGroups ?? [],
  permissions: state.user.permissions,
});

export default connect(mapState)(NoticeBoardPage);
